import React, { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import NavigationBar from './components/NavigationBar';
import PublicDisclosureAlerts from './PublicDisclosureAlerts';
import { AuthContext } from './AuthContext';

const PreviewError = () => (
  <div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200" role="alert">
    <h1 className="text-2xl font-black">Shadow preview unavailable</h1>
    <p className="mt-2 text-sm">This operator preview is available only to an authorized administrator while Public Disclosure Alerts remain in shadow mode.</p>
  </div>
);

const AdminPublicDisclosurePreview = () => {
  const { axiosInstance } = useContext(AuthContext);
  const preview = useQuery({
    queryKey: ['admin', 'public-disclosures', 'shadow-preview'],
    queryFn: async () => (await axiosInstance.get('/api/admin/public-disclosures/shadow-preview')).data,
    retry: false,
    staleTime: 60000,
  });
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <NavigationBar />
      <main className="mx-auto max-w-6xl px-4 pb-12 pt-24 sm:px-6">
        {preview.isLoading ? <p className="text-sm text-slate-600 dark:text-slate-300">Loading shadow preview…</p> : preview.isError || !preview.data ? <PreviewError /> : <>
          <section className="rounded-2xl border-2 border-amber-500 bg-amber-50 p-5 text-amber-950 dark:border-amber-400 dark:bg-amber-950/40 dark:text-amber-100" role="status">
            <p className="text-sm font-black tracking-wide">SHADOW PREVIEW — NOT CUSTOMER VISIBLE</p>
            <p className="mt-1 text-sm">This page uses your saved preferences and stored disclosure data. It cannot deliver a Bell alert or Daily Briefing.</p>
          </section>
          <PublicDisclosureAlerts previewData={preview.data} />
          <section className="mt-6 grid gap-6 lg:grid-cols-2">
            <article className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
              <h2 className="text-lg font-black">Bell preview</h2>
              {preview.data.bell?.payload ? <><p className="mt-3 font-bold">{preview.data.bell.payload.title}</p><p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{preview.data.bell.payload.body}</p><p className="mt-3 text-xs font-bold uppercase tracking-wide text-slate-500">{preview.data.bell.status} only — no alert delivered</p></> : <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">Suppressed by saved preferences or relevance rules.</p>}
            </article>
            <article className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
              <h2 className="text-lg font-black">Daily Briefing preview</h2>
              {preview.data.daily_briefing?.text ? <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-6 text-slate-700 dark:text-slate-200">{preview.data.daily_briefing.text}</pre> : <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">No qualifying disclosures for your saved preferences.</p>}
            </article>
          </section>
        </>}
      </main>
    </div>
  );
};

export default AdminPublicDisclosurePreview;
